// client/src/components/admin/project/OngoingProjectList.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../../../styles/admin/project/OngoingProject.css';
import ProjectDetailModal from '../../marketer/project/modals/ProjectDetailModal';
import ApplicationsViewModal from './ApplicationsViewModal';
import ResultStatusModal from './ResultStatusModal';
import CPSSettlementModal from './CPSSettlementModal';
import ProjectHistoryModal from './ProjectHistoryModal';
import DescriptionModal from '../../marketer/project/modals/DescriptionModal';

const API_URL = process.env.REACT_APP_API_URL;

// KST 기준 오늘 00:00 timestamp (UTC epoch 기반)
function getKSTTodayStart() {
  const KST_OFFSET = 9 * 60 * 60 * 1000;
  const now = Date.now();
  return new Date(Math.floor((now + KST_OFFSET) / 86400000) * 86400000 - KST_OFFSET);
}

// DB 날짜(ISO string 또는 Date)가 잠겼는지 여부 (KST 기준 과거 날짜)
function isFieldLocked(dateValue) {
  if (!dateValue) return false;
  return new Date(dateValue) < getKSTTodayStart();
}

// input type="date"용 YYYY-MM-DD 변환 (KST 기준)
function toDateInputValue(dateValue) {
  if (!dateValue) return '';
  const d = new Date(new Date(dateValue).getTime() + 9 * 60 * 60 * 1000);
  return d.toISOString().split('T')[0];
}

export default function OngoingProjectList() {
  const selectSeqRef = useRef(0);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // 모달 상태
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showApplicationsModal, setShowApplicationsModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showCPSModal, setShowCPSModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  // 제목/설명 수정 모달
  const [showEditInfoModal, setShowEditInfoModal] = useState(false);
  const [nameDraft, setNameDraft]       = useState('');
  const [descDraft, setDescDraft]       = useState('');
  const [originalName, setOriginalName] = useState('');
  const [originalDesc, setOriginalDesc] = useState('');
  const [isSaving, setIsSaving]         = useState(false);
  // 수정 대상 프로젝트 ID — 모달 오픈 시점에 고정 (열린 동안 선택 변경 방어)
  const editingProjectIdRef = useRef(null);
  const [showDescModal, setShowDescModal] = useState(false);

  // 일정 수정 모달
  const [scheduleModal, setScheduleModal]     = useState(false);
  const [cpsRounds, setCpsRounds]             = useState([]);
  const [cpsLoading, setCpsLoading]           = useState(false);
  const [scheduleForm, setScheduleForm]       = useState({
    recruitEnd: '',
    submitEnd: '',
    settlementDates: []
  });
  const [scheduleSaving, setScheduleSaving]   = useState(false);
  const [scheduleError, setScheduleError]     = useState('');
  const scheduleEditingProjectIdRef = useRef(null);
  const scheduleEditingInfoRef      = useRef(null);
  
  // 검색 및 페이지네이션 상태
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 20;

  // 프로젝트 선택 및 상세 정보 조회
  const handleSelectProject = useCallback(async (project) => {
    const seq = ++selectSeqRef.current;
    setSelectedProject(project);
    setLoadingDetail(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/projects/${project.id}/completion-detail`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (seq !== selectSeqRef.current) return; // 이전 요청 결과 무시
      if (data.ok) {
        setSelectedProject({ ...project, ...data.project });
      } else {
        console.error('상세 정보 조회 실패:', data.message);
      }
    } catch (error) {
      if (seq !== selectSeqRef.current) return;
      console.error('상세 정보 조회 오류:', error);
    } finally {
      if (seq === selectSeqRef.current) {
        setLoadingDetail(false);
      }
    }
  }, []);

  // 프로젝트 목록 조회
  const fetchProjects = useCallback(async (preferredProjectId) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: limit
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const res = await fetch(`${API_URL}/api/admin/projects/ongoing-list?${params}`, {
        credentials: 'include'
      });
      const data = await res.json();
      
      if (data.ok) {
        setProjects(data.projects || []);
        setTotalCount(data.total || 0);
        setTotalPages(data.totalPages || 1);
        
        // 첫 번째 프로젝트 자동 선택 (상세 정보 포함)
        if (data.projects && data.projects.length > 0) {
          const target = (preferredProjectId && data.projects.find(p => p.id === preferredProjectId))
            || data.projects[0];
          handleSelectProject(target);
        } else {
          setSelectedProject(null);
        }
      } else {
        alert('목록 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('진행중인 프로젝트 목록 조회 오류:', error);
      alert('목록 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, handleSelectProject]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // 프로젝트 상세보기 모달
  const handleShowProjectDetail = () => {
    if (selectedProject?.fullProjectData) {
      setShowProjectModal(true);
    }
  };

  // 인플루언서 선정 계약 상세 모달
  const handleShowApplications = () => {
    if (selectedProject?.id) {
      setShowApplicationsModal(true);
    }
  };

  // 결과물 상태보기 모달
  const handleShowResultStatus = () => {
    if (selectedProject?.id) {
      setShowResultModal(true);
    }
  };

  // CPS 결산 상태보기 모달
  const handleShowCPSSettlement = () => {
    if (selectedProject?.id) {
      setShowCPSModal(true);
    }
  };

  // 검색 실행
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  // 검색 초기화
  const handleResetSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Enter 키로 검색
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 페이지 변경
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // serviceType별 현재 제목 추출 (fullProjectData 단일 소스 우선)
  const getCurrentTitle = (p) => {
    const src = p?.fullProjectData || p;
    if (src?.serviceType === 'visit') return src.product?.placeName || '';
    if (src?.serviceType === 'free')  return src.product?.customTitle || '';
    return src?.product?.name || '';
  };

  const getTitleLabel = (serviceType) => {
    if (serviceType === 'visit') return '장소명';
    if (serviceType === 'free')  return '자유 제목';
    return '제품명';
  };

  // 수정 모달 열기
  const openInfoEdit = () => {
    if (!selectedProject?.fullProjectData || loadingDetail) return;
    editingProjectIdRef.current = selectedProject.id; // 저장 대상 ID 고정
    const title = getCurrentTitle(selectedProject);
    const desc  = selectedProject.fullProjectData.description || '';
    setNameDraft(title);
    setDescDraft(desc);
    setOriginalName(title);
    setOriginalDesc(desc);
    setShowDescModal(false); // 이전 편집 세션의 Quill 모달 잔존 방어
    setShowEditInfoModal(true);
  };

  // 일정 수정 모달 오픈
  const handleOpenScheduleModal = async () => {
    const proj = selectedProject;
    if (!proj?.fullProjectData) return;

    scheduleEditingProjectIdRef.current = proj.id;
    scheduleEditingInfoRef.current = { scheduleInfo: proj.scheduleInfo || {}, hasCPS: proj.hasCPS };

    const scheduleInfo = proj.scheduleInfo || {};
    const initForm = {
      recruitEnd: toDateInputValue(scheduleInfo.recruitEnd),
      submitEnd:  toDateInputValue(scheduleInfo.submitEnd),
      settlementDates: (scheduleInfo.settlementDates || []).map(toDateInputValue)
    };
    setScheduleForm(initForm);
    setScheduleError('');
    setCpsRounds([]);

    if (proj.hasCPS) {
      setCpsLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/admin/projects/${proj.id}/cps-settlements`, {
          credentials: 'include'
        });
        const data = await res.json();
        if (!res.ok || !data.ok) throw new Error(data.message || 'CPS 조회 실패');

        const today = getKSTTodayStart();
        const rounds = (data.rounds || []).map(r => ({
          roundNumber: r.roundNumber,
          scheduledDate: r.scheduledDate,
          status: r.status,
          locked: r.status !== 'ready' || new Date(r.scheduledDate) < today
        }));
        setCpsRounds(rounds);

        const cpsBasedDates = rounds.map(r => toDateInputValue(r.scheduledDate));
        setScheduleForm(f => ({ ...f, settlementDates: cpsBasedDates }));
      } catch (err) {
        alert(`일정 수정 모달을 열 수 없습니다: ${err.message}`);
        setCpsLoading(false);
        return;
      }
      setCpsLoading(false);
    }

    setScheduleModal(true);
  };

  // 일정 수정 저장
  const handleScheduleSave = async () => {
    const projectId = scheduleEditingProjectIdRef.current;
    const { scheduleInfo, hasCPS } = scheduleEditingInfoRef.current || {};
    if (!projectId || !scheduleInfo) return;

    setScheduleSaving(true);
    setScheduleError('');

    const recruitEndLocked = isFieldLocked(scheduleInfo.recruitEnd);
    const submitEndLocked  = isFieldLocked(scheduleInfo.submitEnd);

    const body = {};
    if (!recruitEndLocked && scheduleForm.recruitEnd) {
      body.recruitEnd = scheduleForm.recruitEnd;
    }
    if (!submitEndLocked && scheduleForm.submitEnd) {
      body.submitEnd = scheduleForm.submitEnd;
    }
    if (hasCPS && scheduleForm.settlementDates.length > 0) {
      body.settlementDates = scheduleForm.settlementDates;
    }

    try {
      const res = await fetch(`${API_URL}/api/admin/projects/${projectId}/schedule`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      const data = await res.json();

      if (!res.ok || !data.ok) {
        setScheduleError(getScheduleErrorMessage(data.message));
        return;
      }

      setScheduleModal(false);
      fetchProjects(projectId);
    } catch (err) {
      setScheduleError('저장 중 오류가 발생했습니다.');
    } finally {
      setScheduleSaving(false);
    }
  };

  // 저장 — 변경된 필드만 전송
  const saveProjectInfo = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const body = {};
      if (nameDraft !== originalName) body.productName = nameDraft;
      if (descDraft !== originalDesc) body.description = descDraft;

      if (Object.keys(body).length === 0) {
        setShowEditInfoModal(false);
        return; // finally → setIsSaving(false) 실행됨
      }

      // 클라이언트 사전 검증: plain text 길이 (서버와 동일 기준)
      if (body.description !== undefined) {
        const plainLen = body.description.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '').length;
        if (plainLen > 2000) {
          alert('설명은 텍스트 기준 2000자 이하로 입력해주세요.');
          return; // finally → setIsSaving(false) 실행됨
        }
      }

      // editingProjectIdRef: 모달 오픈 시점 고정 ID 사용 (열린 동안 선택 변경 방어)
      const projectId = editingProjectIdRef.current;
      const res = await fetch(`${API_URL}/api/admin/projects/${projectId}/info`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.ok) {
        setShowDescModal(false); // Quill 모달도 함께 닫기
        setShowEditInfoModal(false);
        fetchProjects(projectId); // 수정한 프로젝트를 우선 선택하여 목록 갱신
      } else {
        const msgMap = {
          PRODUCT_NAME_EMPTY:      '제목을 입력해주세요.',
          PRODUCT_NAME_TOO_LONG:   '제목은 100자 이하로 입력해주세요.',
          DESCRIPTION_TOO_LONG:    '설명은 텍스트 기준 2000자 이하로 입력해주세요.',
          INVALID_STATUS:          '이 상태의 프로젝트는 수정할 수 없습니다.',
          INVALID_SERVICE_TYPE:    '지원하지 않는 서비스 타입입니다.',
          NOTHING_TO_UPDATE:       '변경된 내용이 없습니다.',
          INVALID_TYPE:            '입력값 형식이 올바르지 않습니다.',
          INVALID_ID:              '프로젝트 ID가 올바르지 않습니다.',
          NOT_FOUND:               '프로젝트를 찾을 수 없습니다.',
          SERVER_ERROR:            '서버 오류가 발생했습니다.',
          PAYLOAD_TOO_LARGE:       '입력 내용이 너무 큽니다.',
        };
        alert(msgMap[data.message] || '수정 실패: ' + data.message);
      }
    } catch {
      alert('네트워크 오류가 발생했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  // 날짜 포맷팅
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  // 날짜 시간 포맷팅
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  // 상태 한글 변환
  const getStatusText = (status) => {
    const statusMap = {
      draft: '작성중',
      open: '진행중',
      closed: '마감',
      canceled: '취소됨',
      archived: '보관됨'
    };
    return statusMap[status] || status;
  };

  // 페이지 번호 생성 (현재 페이지 기준 앞뒤 2개씩)
  const getPageNumbers = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  return (
    <div className="ongoing-project-list">
      {/* 왼쪽: 프로젝트 목록 */}
      <div className="ongoing-project-list__sidebar">
        {/* 헤더 */}
        <div className="ongoing-project-list__sidebar-header">
          <h2 className="ongoing-project-list__sidebar-title">
            진행중인 프로젝트 ({totalCount})
          </h2>
        </div>

        {/* 검색 */}
        <div className="ongoing-project-list__search">
          <div className="ongoing-project-list__search-controls">
            <input
              type="text"
              placeholder="프로젝트 번호 또는 광고주명 검색"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleKeyPress}
              className="ongoing-project-list__search-input"
            />
            <button
              onClick={handleSearch}
              className="ongoing-project-list__search-button"
            >
              검색
            </button>
          </div>
          {searchTerm && (
            <div className="ongoing-project-list__search-info">
              검색어: "{searchTerm}"
              <button
                onClick={handleResetSearch}
                className="ongoing-project-list__search-reset"
              >
                초기화
              </button>
            </div>
          )}
        </div>

        {/* 목록 */}
        <div className="ongoing-project-list__projects">
          {loading && (
            <div className="ongoing-project-list__loading">
              로딩 중...
            </div>
          )}

          {!loading && projects.length === 0 && (
            <div className="ongoing-project-list__empty">
              {searchTerm ? '검색 결과가 없습니다.' : '진행중인 프로젝트가 없습니다.'}
            </div>
          )}

          {!loading && projects.map((project) => (
            <div
              key={project.id}
              onClick={() => handleSelectProject(project)}
              className={`ongoing-project-list__project-item ${
                selectedProject?.id === project.id ? 'ongoing-project-list__project-item--selected' : ''
              }`}
            >
              <div className="ongoing-project-list__project-number">
                {project.projectNumber}
              </div>
              <div className="ongoing-project-list__project-marketer">
                {project.marketerName}
              </div>
              <div className="ongoing-project-list__project-date">
                {formatDate(project.createdAt)}
              </div>
            </div>
          ))}
        </div>

        {/* 페이지네이션 */}
        {!loading && totalPages > 1 && (
          <div className="ongoing-project-list__pagination">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="ongoing-project-list__pagination-btn"
            >
              처음
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="ongoing-project-list__pagination-btn"
            >
              이전
            </button>
            
            {getPageNumbers().map(pageNum => (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`ongoing-project-list__pagination-btn ${
                  currentPage === pageNum ? 'ongoing-project-list__pagination-btn--active' : ''
                }`}
              >
                {pageNum}
              </button>
            ))}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ongoing-project-list__pagination-btn"
            >
              다음
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="ongoing-project-list__pagination-btn"
            >
              마지막
            </button>
          </div>
        )}
      </div>

      {/* 오른쪽: 프로젝트 상세 */}
      <div className="ongoing-project-list__detail">
        {selectedProject ? (
          <>
            {/* 고정 헤더 및 버튼 영역 */}
            <div className="ongoing-project-list__detail-header">
              <h2 className="ongoing-project-list__detail-title">
                프로젝트 상세 정보
              </h2>

              {/* 버튼 영역 */}
              <div className="ongoing-project-list__actions">
                {/* 프로젝트 상세보기 버튼 */}
                <button
                  onClick={handleShowProjectDetail}
                  disabled={!selectedProject?.fullProjectData || loadingDetail}
                  className={`ongoing-project-list__action-btn ${
                    selectedProject?.fullProjectData && !loadingDetail 
                      ? 'ongoing-project-list__action-btn--primary' 
                      : 'ongoing-project-list__action-btn--disabled'
                  }`}
                >
                  {loadingDetail ? '⏳ 로딩 중...' : '📄 프로젝트 상세보기'}
                </button>

                {/* 인플루언서 선정 계약 상세 버튼 */}
                <button
                  onClick={handleShowApplications}
                  disabled={!selectedProject.id}
                  className={`ongoing-project-list__action-btn ${
                    selectedProject.id 
                      ? 'ongoing-project-list__action-btn--success' 
                      : 'ongoing-project-list__action-btn--disabled'
                  }`}
                >
                  📊 인플루언서 선정 계약 상세
                </button>

                {/* 결과물 상태보기 버튼 */}
                <button
                  onClick={handleShowResultStatus}
                  disabled={!selectedProject.id}
                  className={`ongoing-project-list__action-btn ${
                    selectedProject.id 
                      ? 'ongoing-project-list__action-btn--warning' 
                      : 'ongoing-project-list__action-btn--disabled'
                  }`}
                >
                  📋 결과물 상태보기
                </button>

                {/* CPS 결산 상태보기 버튼 */}
                <button
                  onClick={handleShowCPSSettlement}
                  disabled={!selectedProject.id}
                  className={`ongoing-project-list__action-btn ${
                    selectedProject.id 
                      ? 'ongoing-project-list__action-btn--purple' 
                      : 'ongoing-project-list__action-btn--disabled'
                  }`}
                >
                  💰 CPS 결산 상태보기
                </button>

                {/* 이력 관리 버튼 */}
                <button
                  onClick={() => setShowHistoryModal(true)}
                  disabled={!selectedProject.id}
                  className={`ongoing-project-list__action-btn ${
                    selectedProject.id 
                      ? 'ongoing-project-list__action-btn--indigo' 
                      : 'ongoing-project-list__action-btn--disabled'
                  }`}
                >
                  📋 이력 관리
                </button>
                {/* 제목/설명 수정 버튼 */}
                <button
                  onClick={openInfoEdit}
                  disabled={!selectedProject?.fullProjectData || loadingDetail}
                  className={`ongoing-project-list__action-btn ${
                    selectedProject?.fullProjectData && !loadingDetail
                      ? 'ongoing-project-list__action-btn--primary'
                      : 'ongoing-project-list__action-btn--disabled'
                  }`}
                >
                  ✏️ 제목/설명 수정
                </button>
                {/* 일정 수정 버튼 */}
                <button
                  onClick={handleOpenScheduleModal}
                  disabled={!selectedProject?.fullProjectData || loadingDetail || cpsLoading}
                  className={`ongoing-project-list__action-btn ${
                    selectedProject?.fullProjectData && !loadingDetail && !cpsLoading
                      ? 'ongoing-project-list__action-btn--primary'
                      : 'ongoing-project-list__action-btn--disabled'
                  }`}
                >
                  📅 일정 수정
                </button>
              </div>
            </div>

            {/* 스크롤 가능한 내용 영역 */}
            <div className="ongoing-project-list__detail-content">
              {/* 기본 정보 섹션 */}
              <div className="ongoing-project-list__section">
                <h3 className="ongoing-project-list__section-title">
                  📌 기본 정보
                </h3>
                <div className="ongoing-project-list__field">
                  <div className="ongoing-project-list__field-label">프로젝트 번호</div>
                  <div className="ongoing-project-list__field-value">{selectedProject.projectNumber}</div>
                </div>
                <div className="ongoing-project-list__field">
                  <div className="ongoing-project-list__field-label">광고주명</div>
                  <div className="ongoing-project-list__field-value">{selectedProject.marketerName}</div>
                </div>
                <div className="ongoing-project-list__field">
                  <div className="ongoing-project-list__field-label">광고주 ID</div>
                  <div className="ongoing-project-list__field-value">{selectedProject.marketerUserId}</div>
                </div>
                <div className="ongoing-project-list__field">
                  <div className="ongoing-project-list__field-label">상품명</div>
                  <div className="ongoing-project-list__field-value">{selectedProject.productName || '-'}</div>
                </div>
                <div className="ongoing-project-list__field">
                  <div className="ongoing-project-list__field-label">상태</div>
                  <div className="ongoing-project-list__field-value">{getStatusText(selectedProject.status)}</div>
                </div>
              </div>

              {/* 일정 정보 섹션 */}
              {selectedProject.scheduleInfo && (
                <div className="ongoing-project-list__section">
                  <h3 className="ongoing-project-list__section-title">
                    📅 일정 정보
                  </h3>
                  <div className="ongoing-project-list__field">
                    <div className="ongoing-project-list__field-label">프로젝트 생성일</div>
                    <div className="ongoing-project-list__field-value">{formatDateTime(selectedProject.scheduleInfo.createdAt)}</div>
                  </div>
                  <div className="ongoing-project-list__field">
                    <div className="ongoing-project-list__field-label">모집 및 선정 마감일</div>
                    <div className="ongoing-project-list__field-value">{formatDateTime(selectedProject.scheduleInfo.recruitEnd)}</div>
                  </div>
                  <div className="ongoing-project-list__field">
                    <div className="ongoing-project-list__field-label">결과물 제출 마감일</div>
                    <div className="ongoing-project-list__field-value">{formatDateTime(selectedProject.scheduleInfo.submitEnd)}</div>
                  </div>
                  {selectedProject.scheduleInfo.settlementDates?.length > 0 && (
                    <div className="ongoing-project-list__field">
                      <div className="ongoing-project-list__field-label">CPS 결산일</div>
                      <div className="ongoing-project-list__field-value">
                        {selectedProject.scheduleInfo.settlementDates.map((date, idx) => (
                          <div key={idx} className="ongoing-project-list__settlement-date">
                            {idx + 1}회차: {formatDateTime(date)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 결제 및 모집 정보 섹션 */}
              {(selectedProject.paymentDetails || selectedProject.recruitHeadcount !== undefined || selectedProject.contractedCount !== undefined || selectedProject.hasCPS !== undefined) && (
                <div className="ongoing-project-list__section">
                  <h3 className="ongoing-project-list__section-title">
                    💰 결제 및 모집 정보
                  </h3>
                  {selectedProject.paymentDetails && (
                    <div className="ongoing-project-list__field">
                      <div className="ongoing-project-list__field-label">결제 금액</div>
                      <div className="ongoing-project-list__field-value">
                        {(selectedProject.paymentDetails.deposit || 0).toLocaleString()}pt
                        <div className="ongoing-project-list__payment-breakdown">
                          (기본운영비: {(selectedProject.paymentDetails.baseOp || 0).toLocaleString()}pt, 
                          리워드: {(selectedProject.paymentDetails.rewardTotal || 0).toLocaleString()}pt
                          {selectedProject.paymentDetails.perBudget > 0 && 
                            `, CPS예산: ${selectedProject.paymentDetails.perBudget.toLocaleString()}pt`
                          })
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="ongoing-project-list__field">
                    <div className="ongoing-project-list__field-label">모집 인원</div>
                    <div className="ongoing-project-list__field-value">{selectedProject.recruitHeadcount || 0}명</div>
                  </div>
                  <div className="ongoing-project-list__field">
                    <div className="ongoing-project-list__field-label">계약 인원</div>
                    <div className="ongoing-project-list__field-value ongoing-project-list__field-value--success">
                      {selectedProject.contractedCount || 0}명
                    </div>
                  </div>
                  <div className="ongoing-project-list__field">
                    <div className="ongoing-project-list__field-label">구매당 리워드 설정</div>
                    <div className={`ongoing-project-list__field-value ${
                      selectedProject.hasCPS 
                        ? 'ongoing-project-list__field-value--purple' 
                        : 'ongoing-project-list__field-value--gray'
                    }`}>
                      {selectedProject.hasCPS ? '✅ 설정됨' : '❌ 미설정'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="ongoing-project-list__detail-empty">
            프로젝트를 선택해주세요.
          </div>
        )}
      </div>

      {/* 제목/설명 수정 모달 */}
      {showEditInfoModal && selectedProject && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 24, width: 600, maxHeight: '80vh', overflowY: 'auto' }}>
            <h3 style={{ marginTop: 0 }}>프로젝트 제목/설명 수정</h3>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>
                카드 제목 ({getTitleLabel(selectedProject?.serviceType)})
                <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 8 }}>
                  {nameDraft.length}/100
                </span>
              </label>
              <input
                type="text"
                value={nameDraft}
                onChange={e => setNameDraft(e.target.value)}
                maxLength={100}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4, boxSizing: 'border-box' }}
                placeholder="프로젝트 제목을 입력하세요"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>
                프로젝트 설명
                <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 8, fontSize: 12 }}>
                  {descDraft
                    ? `${descDraft.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '').length}/2000자 작성됨`
                    : '미작성'}
                </span>
              </label>
              <button
                type="button"
                onClick={() => setShowDescModal(true)}
                disabled={isSaving}
                style={{
                  width: '100%', padding: '10px 14px', border: '1px solid #2563eb',
                  borderRadius: 4, background: '#eff6ff', color: '#2563eb',
                  cursor: isSaving ? 'not-allowed' : 'pointer', textAlign: 'left', fontSize: 14,
                  opacity: isSaving ? 0.6 : 1
                }}
              >
                ✏️ 설명 편집 (Quill 에디터 열기)
              </button>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowDescModal(false); setShowEditInfoModal(false); }}
                disabled={isSaving}
                style={{ padding: '8px 16px', borderRadius: 4 }}
              >
                취소
              </button>
              <button
                onClick={saveProjectInfo}
                disabled={isSaving}
                style={{ background: isSaving ? '#93c5fd' : '#2563eb', color: '#fff', padding: '8px 16px', borderRadius: 4, border: 'none', cursor: isSaving ? 'not-allowed' : 'pointer' }}
              >
                {isSaving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 일정 수정 모달 */}
      {scheduleModal && selectedProject && (() => {
        const scheduleInfo = scheduleEditingInfoRef.current?.scheduleInfo || {};
        const recruitEndLocked = isFieldLocked(scheduleInfo.recruitEnd);
        const submitEndLocked  = isFieldLocked(scheduleInfo.submitEnd);

        const showRecruitWarning =
          selectedProject.status === 'open' &&
          !recruitEndLocked &&
          scheduleForm.recruitEnd &&
          scheduleForm.recruitEnd > toDateInputValue(scheduleInfo.recruitEnd);

        const showSubmitWarning =
          !submitEndLocked &&
          scheduleForm.submitEnd &&
          scheduleForm.submitEnd !== toDateInputValue(scheduleInfo.submitEnd);

        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', borderRadius: 8, padding: 24, width: 520, maxHeight: '80vh', overflowY: 'auto' }}>
              <h3 style={{ marginTop: 0 }}>일정 수정</h3>

              {/* 모집 마감일 */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>모집 마감일</label>
                {recruitEndLocked ? (
                  <div>
                    <span>{toDateInputValue(scheduleInfo.recruitEnd)}</span>
                    <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>변경 불가 (이미 지남)</span>
                  </div>
                ) : (
                  <input
                    type="date"
                    value={scheduleForm.recruitEnd}
                    onChange={e => setScheduleForm(f => ({ ...f, recruitEnd: e.target.value }))}
                    style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 4 }}
                  />
                )}
                {showRecruitWarning && (
                  <p style={{ color: '#e67e22', fontSize: 13, marginTop: 6 }}>
                    ⚠️ 모집 마감일 연장 시 인플루언서 신규 지원과 광고주 추가 선정이 다시 활성화됩니다.
                  </p>
                )}
              </div>

              {/* 제출 마감일 */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>콘텐츠 제출 마감일</label>
                {submitEndLocked ? (
                  <div>
                    <span>{toDateInputValue(scheduleInfo.submitEnd)}</span>
                    <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>변경 불가 (이미 지남)</span>
                  </div>
                ) : (
                  <input
                    type="date"
                    value={scheduleForm.submitEnd}
                    onChange={e => setScheduleForm(f => ({ ...f, submitEnd: e.target.value }))}
                    style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 4 }}
                  />
                )}
                {showSubmitWarning && (
                  <p style={{ color: '#e67e22', fontSize: 13, marginTop: 6 }}>
                    ⚠️ 제출마감 변경은 이미 미제출 처리된 결과물을 자동 복구하지 않습니다.
                  </p>
                )}
              </div>

              {/* CPS 결산일 */}
              {selectedProject.hasCPS && cpsRounds.map((round, i) => (
                <div key={round.roundNumber} style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>{round.roundNumber}회차 결산일</label>
                  {round.locked ? (
                    <div>
                      <span>{toDateInputValue(round.scheduledDate)}</span>
                      <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>이미 처리됨</span>
                    </div>
                  ) : (
                    <input
                      type="date"
                      value={scheduleForm.settlementDates[i] || ''}
                      onChange={e => {
                        const newDates = [...scheduleForm.settlementDates];
                        newDates[i] = e.target.value;
                        setScheduleForm(f => ({ ...f, settlementDates: newDates }));
                      }}
                      style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 4 }}
                    />
                  )}
                </div>
              ))}

              {scheduleError && (
                <p style={{ color: '#e74c3c', fontSize: 13, marginBottom: 12 }}>{scheduleError}</p>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setScheduleModal(false)}
                  disabled={scheduleSaving}
                  style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: 'pointer' }}
                >
                  취소
                </button>
                <button
                  onClick={handleScheduleSave}
                  disabled={scheduleSaving}
                  style={{ padding: '8px 16px', background: '#3498db', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                >
                  {scheduleSaving ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 설명 Quill 에디터 모달 (관리자용 — onSave로 descDraft만 업데이트, API 미호출) */}
      {/* z-index: DescriptionModal CSS 1000, 관리자 수정 모달도 1000. DOM 렌더 순서상 뒤에 오므로 정상 표시됨. 충돌 시 DescriptionModal.css를 1010으로 상향. */}
      <DescriptionModal
        open={showDescModal}
        onClose={() => setShowDescModal(false)}
        initialDescription={descDraft}
        onSave={(html) => setDescDraft(html)}
      />

      {/* 프로젝트 상세보기 모달 */}
      <ProjectDetailModal 
        open={showProjectModal}
        onClose={() => setShowProjectModal(false)}
        project={selectedProject?.fullProjectData}
      />

      {/* 인플루언서 선정 계약 상세 모달 */}
      <ApplicationsViewModal
        open={showApplicationsModal}
        onClose={() => setShowApplicationsModal(false)}
        projectId={selectedProject?.id}
        projectNumber={selectedProject?.projectNumber}
      />

      {/* 결과물 상태보기 모달 */}
      <ResultStatusModal
        open={showResultModal}
        onClose={() => setShowResultModal(false)}
        projectId={selectedProject?.id}
        projectNumber={selectedProject?.projectNumber}
      />

      {/* CPS 결산 상태보기 모달 */}
      <CPSSettlementModal
        open={showCPSModal}
        onClose={() => setShowCPSModal(false)}
        projectId={selectedProject?.id}
        projectNumber={selectedProject?.projectNumber}
      />

      {/* 이력 관리 모달 */}
      <ProjectHistoryModal
        open={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        projectId={selectedProject?.id}
        projectNumber={selectedProject?.projectNumber}
      />
    </div>
  );
}

function getScheduleErrorMessage(code) {
  const messages = {
    RECRUIT_END_LOCKED:              '모집 마감일은 이미 지나 수정할 수 없습니다.',
    SUBMIT_END_LOCKED:               '제출 마감일은 이미 지나 수정할 수 없습니다.',
    SETTLEMENT_DATE_LOCKED:          '이미 처리된 결산 회차의 날짜는 변경할 수 없습니다.',
    SETTLEMENT_DATA_INTEGRITY_ERROR: '결산 데이터 오류가 발생했습니다. 관리자에게 문의하세요.',
    DATE_IN_PAST:                    '새 날짜는 오늘 이후여야 합니다.',
    INVALID_DATE_ORDER:              '모집 마감일은 제출 마감일보다 이전이어야 합니다.',
    SUBMIT_END_TOO_EARLY:            '제출 마감일은 모집 마감일 기준 7일 이후여야 합니다.',
    SETTLEMENT_DATE_TOO_EARLY:       '결산일은 제출 마감일 기준 10일 이후, 회차 간 10일 이상이어야 합니다.',
    SETTLEMENT_DATES_COUNT_MISMATCH: '결산일 개수가 맞지 않습니다.',
    NOTHING_TO_UPDATE:               '변경된 내용이 없습니다.',
    INVALID_DATE_VALUE:              '날짜 형식이 올바르지 않습니다.',
  };
  return messages[code] || `오류가 발생했습니다. (${code})`;
}

